import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, tokens, unwrap, type AuthUser } from './api';

interface AuthState {
  user: AuthUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** True when the user holds the permission, or is a super admin. */
  can: (permission: string) => boolean;
  hasRole: (...roles: string[]) => boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * `/auth/login` returns roles as codes, `/auth/me` returns them as `{code, name}` objects.
 *
 * Normalising here rather than at each call site means a restored session behaves exactly
 * like a fresh one — before this, `hasRole()` silently returned false after a page reload
 * and the role line rendered as "[object Object]".
 */
type RawUser = Omit<AuthUser, 'roles'> & {
  roles: Array<string | { code: string }>;
  permissions?: string[];
};

function normaliseUser(raw: RawUser): AuthUser {
  return {
    ...raw,
    roles: raw.roles.map((role) => (typeof role === 'string' ? role : role.code)),
  } as AuthUser;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => tokens.user());
  const [loading, setLoading] = useState(true);

  // Confirm a stored session is still valid before trusting it — a token in localStorage
  // proves nothing once it has expired or been revoked.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tokens.access()) {
        setLoading(false);
        return;
      }
      try {
        const me = await unwrap<RawUser>(api.auth.get('/auth/me'));
        if (!cancelled) {
          const normalised = normaliseUser(me);
          setUser(normalised);
          tokens.save(tokens.access()!, tokens.refresh() ?? '', normalised);
        }
      } catch {
        if (!cancelled) {
          tokens.clear();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The API client raises this when a refresh fails; the UI must drop to the sign-in screen.
  useEffect(() => {
    const onExpired = () => {
      tokens.clear();
      setUser(null);
    };
    window.addEventListener('nuat:session-expired', onExpired);
    return () => window.removeEventListener('nuat:session-expired', onExpired);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const data = await unwrap<{
      accessToken: string; refreshToken: string; user: RawUser;
    }>(api.auth.post('/auth/login', { email, password }));
    const user = normaliseUser(data.user);
    tokens.save(data.accessToken, data.refreshToken, user);
    setUser(user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.auth.post('/auth/logout', { refreshToken: tokens.refresh() });
    } catch {
      // Signing out locally matters more than the server acknowledging it.
    }
    tokens.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    signIn,
    signOut,
    loading,
    can: (permission) =>
      !!user && (user.permissions.includes('*') || user.permissions.includes(permission)),
    hasRole: (...roles) => !!user && roles.some((role) => user.roles.includes(role)),
  }), [user, signIn, signOut, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
